import React, {Context, useCallback, useContext, useEffect, useState} from "react";
import { Checkbox } from "../inputs/Checkbox";
import { DatePicker } from "../inputs/DatePicker";
import { IParametrizedFieldContext } from "../../contexts/ParametrizedFieldContext";
import { FIELD_TYPE, IReactField } from "../../model/reactField";
import { strings } from "../../strings/strings";
import './index.scss';
import { v4 as uuidv4 } from 'uuid';
import { ISettingOption } from "../../model/settingItem";
import { compareObjects } from "../../utils/compareObjects";
import { SelectOneInput } from "../inputs/SelectOneInput";
import { InputText } from "../inputs/InputText";
import { SelectAutocomplete } from "../inputs/SelectAutocomplete";
import { SelectInfiniteAutocomplete } from "../inputs/SelectAutocompleteInfinite";
import { RadioGroup } from "../inputs/RadioGroup";
import { useFormikContext} from "formik";
import { InputTextMask } from "../inputs/InputTextMask";
import { PlateNumberInput } from "../inputs/PlateNumberInput";
import { SimpleTooltip } from "../SimpleTooltip";
import { Skeleton } from '@material-ui/lab';
import { translit } from "../../utils/utils";
import { OutputLink } from "../OutputLink";
import { ErrorFieldsContext } from "../../contexts/ErrorFieldsContext";
import { FormItem } from "../inputs/FormItem";
import { useDebounceChange } from "./hooks/useDebounceChange";
import { SearchCityResponse } from "../../model/city";

interface IParametrizedFieldProps {
    quotationName: string,
    contextName: string,
    context: Context<IParametrizedFieldContext>,
    numericSelectOptions?: () => any[];
    shortcut?: any;
    charactersToSearch?: number;
    onChangeListener?: (val: any) => void;
    convertContextNameFunction?: (name: string) => string;
    minDate?: any;
    maxDate?: any;
    mask?: string;
    placeholder?: string;
    defaultLabel?: string;
    defaultOptions?: any[];
    fieldInfoText?: string;
    messageInfoObj?: any;
    toUpperCase?: boolean;
    noSpace?: boolean;
    lazyDefaultOptions?: (query?: string, size?: number) => Promise<SearchCityResponse>; 
    plateNumber?: boolean;
    small?: boolean;
    contextPath?: string;
    selectOneRadio?: boolean;
    formatValue?: (value: any) => any;
    formatResponse?: (value: any) => any;
    formatOptions?: (options: any) => any;
    tooltipLabel?: string;
    additionalTooltip?: React.ReactElement;
    disabled?: boolean;
    additionalFieldInfo?: string;
    isProcessingOpenOnUpdate?: boolean,
    listenerDependency?: any,
    customValidationRule?: (value: any) => any;
    infiniteScroll?: boolean;
}

export const ParametrizedField: React.FC<IParametrizedFieldProps> = (props) => {
    const {
        context,
        quotationName,
        contextName,
        numericSelectOptions,
        defaultOptions,
        shortcut,
        charactersToSearch,
        formatResponse,
        formatValue,
        onChangeListener,
        convertContextNameFunction,
        minDate,
        maxDate,
        mask,
        placeholder,
        defaultLabel,
        fieldInfoText,
        messageInfoObj,
        formatOptions,
        toUpperCase,
        noSpace,
        lazyDefaultOptions,
        plateNumber,
        small,
        contextPath,
        selectOneRadio,
        tooltipLabel,
        additionalTooltip,
        disabled,
        additionalFieldInfo,
        isProcessingOpenOnUpdate,
        listenerDependency,
        customValidationRule,
        infiniteScroll
    } = props;
    const {
        getFieldByName,
        updateField,
        getFunctionSelectItemsByFieldNameAndFormName,
        fields,
    } = useContext(context);

    const defaultSettingOption = { label: strings.notChosen, value: '00000000-0000-0000-0000-000000000000' };
    const [fieldInfo, setFieldInfo] = useState<IReactField | undefined>();
    const { values, setFieldValue } = useFormikContext();
    const { errorFields } = useContext(ErrorFieldsContext);

    const fieldName = useCallback((name, quotationName) => {
        const transformedName = convertContextNameFunction ? convertContextNameFunction(name) : name.replaceAll("-", ".");
        return quotationName + transformedName;
    }, [convertContextNameFunction])

    useEffect(() => {
        const foundField = getFieldByName(contextName, true, contextPath);
        if (foundField && (!fieldInfo || !compareObjects(fieldInfo, foundField.value))) {
            setFieldInfo(foundField.value);
        }
        //eslint-disable-next-line
    }, [fields]);

    const updateFieldByRequest = (fields: IReactField[]) => {
        fields.forEach((field) => {
            const fullName = fieldName(field.name, quotationName);
            if (field.type === FIELD_TYPE.BOOLEAN_SELECT) {
                setFieldValue(fullName, field.value === 'true');
            }
        })
    }

    const handleChange = async (value: any) => {
        onChangeListener?.(value);
        const fieldsToUpdate = await updateField(fieldInfo.name, value, values, contextPath, !!isProcessingOpenOnUpdate);
        if (fieldsToUpdate) updateFieldByRequest(fieldsToUpdate);
    }

    const {handleDebounceChange} = useDebounceChange(handleChange)

    const onAutocompleteLazyLoading = async (query: string): Promise<ISettingOption[]> => {
        if (fieldInfo?.name) {
            return  await getFunctionSelectItemsByFieldNameAndFormName(fieldInfo.name,
                // @ts-ignore send quotation if exists
                "mainContract", query, values.quotation);
        }

        return [defaultSettingOption];
    }

    const getFormattedOptions = (selectItems?: any) =>
        defaultOptions || (formatOptions ? formatOptions(selectItems) : selectItems);

    const getIsDisabled = (fieldInfo: IReactField) =>
        fieldInfo.isDisabled || disabled === true;

    const getValidation = (fieldInfo: IReactField) => ({
        isRequired: fieldInfo.isRequired,
        maxLength: fieldInfo.maxLength,
        custom: customValidationRule,
    });

    const renderField = (id: string, fieldInfo:IReactField) => {
        const {
            name: fieldNameValue,
            inputLabel,
            value,
            type,
            selectItems,
            isDisabled,
            isError,
            errorMessage,
            isHidden,
            beforeInputRangeMinValue,
            beforeInputRangeMaxValue,
            businessRuleDescriptions
        } = fieldInfo;

        const name = fieldName(fieldNameValue, quotationName);
        const label = defaultLabel || inputLabel;
        const options = getFormattedOptions(selectItems);
        const disabledState = getIsDisabled(fieldInfo);
        const businessRuleDescription = businessRuleDescriptions?.[0]?.description;
        const minDateValue = minDate ?? beforeInputRangeMinValue;
        const maxDateValue = maxDate ?? beforeInputRangeMaxValue;

        if (isError) errorFields.push(id);

        const validation = getValidation(fieldInfo);

        switch (type) {
            case FIELD_TYPE.INPUT_TEXT:
            case FIELD_TYPE.INPUT_TEXT_AREA:
            case FIELD_TYPE.INPUT_TEXT_PHONE:
                if (plateNumber) {
                    return <FormItem name={name} validation={validation}>
                        <PlateNumberInput
                            label={label}
                            disabled={isDisabled}
                            hidden={isHidden}
                            small={small}
                            placeholder={placeholder}
                            error={isError}
                            validationError={errorMessage}
                            formatResponse={formatResponse}
                            onChange={handleDebounceChange}
                        />
                    </FormItem>
                } else if (mask !== undefined) {
                    return <FormItem name={name} validation={validation}>
                        <InputTextMask
                            mask={mask}
                            label={label}
                            disabled={isDisabled}
                            hidden={isHidden}
                            placeholder={placeholder}
                            toUpperCase={toUpperCase}
                            listenerDependency={listenerDependency}
                            error={isError}
                            validationError={errorMessage}
                            onChange={handleDebounceChange}
                        />
                    </FormItem>
                } else {
                    return <FormItem name={name} validation={validation}>
                        <InputText
                            label={label}
                            disabled={isDisabled}
                            hidden={isHidden}
                            listenerDependency={listenerDependency}
                            type={'TEXT'}
                            placeholder={placeholder}
                            error={isError}
                            validationError={errorMessage}
                            onChange={handleDebounceChange}
                            noSpace={noSpace}
                            toUpperCase={toUpperCase}
                        />
                    </FormItem>
                }
            case FIELD_TYPE.ENUM_SELECT:
            case FIELD_TYPE.DICTIONARY_SELECT:
            case FIELD_TYPE.ELASTIC_DICTIONARY:
                if (selectOneRadio) {
                    return <div className={'select-one-wrapper'} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center'
                    }}>
                        <FormItem name={name} validation={validation}>
                            <RadioGroup
                                value={value}
                                options={[...options.map(option => ({
                                    label: option.label,
                                    value: option.value
                                }))]}
                                onChange={handleChange}
                            />
                        </FormItem>
                        {additionalTooltip}
                        <SimpleTooltip
                            id={'travel_is'}
                            placement={'top'}
                            title={<>{businessRuleDescription}</>}
                        >
                            {tooltipLabel}
                        </SimpleTooltip>
                    </div>
                } else return (
                    <FormItem name={name} validation={validation}>
                        <SelectOneInput
                            placeholder={label}
                            options={options}
                            className={'maxWidthForInput'}
                            disabled={isDisabled}
                            hidden={isHidden}
                            error={isError}
                            validationError={errorMessage}
                            formatResponse={formatResponse}
                            formatValue={formatValue}
                            onChange={handleChange}
                        />
                    </FormItem>
                )
            case FIELD_TYPE.BOOLEAN_SELECT:
                return (
                    <FormItem name={name} validation={validation}>
                        <Checkbox
                            label={label}
                            disabled={isDisabled}
                            hidden={isHidden}
                            error={isError}
                            validationError={errorMessage}
                            formatValue={formatValue}
                            onChange={handleChange}
                        />
                    </FormItem>
                )
            case FIELD_TYPE.INPUT_NUMBER:
                if (numericSelectOptions) {
                    let arr = [];
                    numericSelectOptions().forEach(el => arr.push({ "label": el, "value": el }));
                    return <FormItem name={name} validation={validation}>
                        <SelectOneInput
                            placeholder={label}
                            options={arr}
                            className={'maxWidthForInput'}
                            disabled={isDisabled}
                            hidden={isHidden}
                            error={isError}
                            validationError={errorMessage}
                            formatResponse={formatResponse}
                            formatValue={formatValue}
                            onChange={handleChange}
                        />
                    </ FormItem>
                }

                return (
                    <div style={{ display: "flex" }}>
                        <FormItem name={name} validation={validation}>
                            <InputText
                                label={label}
                                disabled={isDisabled}
                                hidden={isHidden}
                                type={'NUMBER'}
                                onChange={handleDebounceChange}
                                error={isError}
                                validationError={errorMessage}
                            />
                            {tooltipLabel && (
                                <div className={'parametrized-field__tooltip'}>
                                    <SimpleTooltip
                                        id={translit('tooltip-number')}
                                        title={<>{tooltipLabel}</>}
                                    >
                                        <div className={'parametrized-field__tooltip-icon'}>
                                            ?
                                        </div>
                                    </SimpleTooltip>
                                </div>
                            )}
                        </FormItem>
                    </div>
                );
            case FIELD_TYPE.INPUT_DECIMAL_NUMBER:
                return (
                    <FormItem name={name} validation={validation}>
                        <InputText
                            label={label}
                            disabled={disabledState}
                            hidden={isHidden}
                            type={"NUMBER"}
                            onChange={handleDebounceChange}
                            error={isError}
                            validationError={errorMessage}
                        />
                    </FormItem>
                );
            case FIELD_TYPE.DATE:
                return (
                    <FormItem name={name} validation={validation}>
                        <DatePicker
                            minDate={minDateValue}
                            maxDate={maxDateValue}
                            placeholder={label}
                            label={label}
                            disabled={disabledState}
                            hidden={isHidden}
                            formatResponse={formatResponse}
                            onChange={handleChange}
                            error={isError}
                            validationError={errorMessage}
                        />
                    </FormItem>
                );
            case FIELD_TYPE.FUNCTION_SELECT:
            case FIELD_TYPE.AUTOCOMPLETE:
                return <FormItem name={name} validation={validation}>
                    {
                        infiniteScroll
                            ?
                            <SelectInfiniteAutocomplete
                                placeholder={label}
                                charactersToSearch={charactersToSearch}
                                loadFunction={lazyDefaultOptions}
                                noOptionsText={strings.noOptionsText}
                                shortcutData={shortcut}
                                disabled={disabledState}
                                hidden={isHidden}
                                formatResponse={formatResponse}
                                formatValue={formatValue}
                                formatOptions={formatOptions}
                                onChange={(value) => handleChange(value?.code)}
                                error={isError}
                                validationError={errorMessage}
                            />
                            :
                            <SelectAutocomplete
                                placeholder={label}
                                charactersToSearch={charactersToSearch}
                                loadFunction={onAutocompleteLazyLoading}
                                noOptionsText={strings.noOptionsText}
                                shortcutData={shortcut}
                                disabled={disabledState}
                                hidden={isHidden}
                                formatResponse={formatResponse}
                                formatValue={formatValue}
                                formatOptions={formatOptions}
                                onChange={(value) => handleChange(value?.code)}
                                error={isError}
                                validationError={errorMessage}
                            />
                    }
                </FormItem>
            case FIELD_TYPE.CHECKBOX_MENU:
                return <div>Unknown type</div>
        }
    }

    let id = 'parametrized-field_' + uuidv4();

    if( !fieldInfo ){
        return <Skeleton variant="text" />
    }

    return (
        <div id={id} className={`parametrized-field${fieldInfo.isHidden ? ' parametrized-field--hidden' : ''}`}>
            {renderField(id, fieldInfo)}
            {fieldInfoText && (
                <SimpleTooltip id={translit(fieldInfoText)} title={
                    <span>{messageInfoObj}</span>}
                >
                    <div className='input-text__field-info'>
                        <OutputLink label={fieldInfoText} variant={"grey"} />
                    </div>
                </SimpleTooltip>
            )}
            {additionalFieldInfo &&
                <div className="parametrized-field__field-description">
                    {additionalFieldInfo}
                </div>}
        </div>
    )
}
